<template>
  <div class="voice-panel" :class="{ 'voice-panel--open': panelOpen, 'voice-panel--call-active': state !== 'idle', 'voice-panel--dragging': dragging }" :style="panelStyle">
    <div class="voice-panel__fab"
      @mousedown="onDragStart"
      @touchstart="onDragStart"
      @click="handleFabClick"
      :class="{ 'voice-panel__fab--active': state !== 'idle', 'voice-panel__fab--dragging': dragging }">
      <VoiceCallSpinner
        ref="spinner"
        :size="spinnerSize"
        :vad-state="vadState"
        :call-status="callStatus"
        :caller-id="callerId"
        :user-info="userInfo"
        :crm-base-url="crmBaseUrl"
        :auth-key="authKey"
        :user-auth-key="userAuthKey"
    :stun-servers="stunServers"
    :turn-servers="turnServers"
    :voice-server-url="voiceServerUrl"
        @state-change="onStateChange"
        @call-start="onCallStart"
        @call-end="onCallEnd"
        @error="onError"
        @stats="onStats"
      />
    </div>

    <Transition name="voice-slide">
      <div v-if="panelOpen && showTranscript" class="voice-panel__transcript">
        <div class="voice-panel__header">
          <div class="voice-panel__header-left">
            <span class="voice-panel__header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                <rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/>
              </svg>
            </span>
            <span class="voice-panel__header-title">Voice AI</span>
            <span class="voice-panel__header-dot" :class="`voice-panel__header-dot--${state}`"></span>
            <span class="voice-panel__header-duration" v-if="duration > 0">{{ formattedDuration }}</span>
          </div>
          <div class="voice-panel__header-actions">
            <button class="voice-panel__btn voice-panel__btn--icon" @click="showTranscript = false" title="Minimize transcript">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
                <polyline points="18 15 12 9 6 15"/>
              </svg>
            </button>
            <button class="voice-panel__btn voice-panel__btn--icon" @click="handleClose" title="Close panel">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="voice-panel__messages" ref="messagesContainer">
          <div v-if="transcriptMessages.length === 0" class="voice-panel__empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="32" height="32">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
            <p v-if="state === 'idle'">Tap the microphone to start a call</p>
            <p v-else-if="state === 'connecting' || state === 'registering'">Establishing connection...</p>
            <p v-else-if="state === 'ringing'">Calling AI assistant...</p>
            <p v-else-if="state === 'connected'">Listening — speak when ready</p>
            <p v-else>No messages yet</p>
          </div>
          <div
            v-for="(msg, idx) in transcriptMessages"
            :key="idx"
            class="voice-panel__message"
            :class="{ 'voice-panel__message--user': msg.role === 'user' }"
          >
            <div class="voice-panel__msg-avatar">
              <svg v-if="msg.role === 'user'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              </svg>
            </div>
            <div class="voice-panel__msg-content">
              <div class="voice-panel__msg-role">{{ msg.role === 'user' ? 'You' : 'AI Assistant' }}</div>
              <div class="voice-panel__msg-text">{{ msg.text }}</div>
              <div v-if="msg.tool" class="voice-panel__msg-tool">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
                  <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/>
                </svg>
                <span>{{ msg.tool }}</span>
              </div>
              <div v-if="msg.toolResult" class="voice-panel__tool-result">
                <pre>{{ formatToolResult(msg.toolResult) }}</pre>
              </div>
              <div class="voice-panel__msg-time">{{ formatTime(msg.ts) }}</div>
            </div>
          </div>
        </div>

        <div class="voice-panel__actions" v-if="state === 'connected'">
          <button class="voice-panel__btn voice-panel__btn--primary" @click="handleHangup">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            End Call
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import VoiceCallSpinner from './VoiceCallSpinner.vue'

export default {
  name: 'VoiceCallPanel',
  components: { VoiceCallSpinner },
  props: {
    callerId: { type: String, default: '' },
    userInfo: { type: Object, default: () => ({}) },
    crmBaseUrl: { type: String, default: '' },
    authKey: { type: String, default: '' },
    userAuthKey: { type: String, default: '' },
    stunServers: {
      type: Array,
      default: () => [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    },
    turnServers: {
      type: Array,
      default: () => [],
    },
    voiceServerUrl: { type: String, default: '' },
  },
  emits: ['callStart', 'callEnd', 'error'],
  data() {
    return {
      state: 'idle',
      sessionId: null,
      panelOpen: false,
      showTranscript: false,
      transcriptMessages: [],
      duration: 0,
      spinnerSize: 56,
      eventsWs: null,
      vadState: 'idle',
      callStatus: '',
      posX: 0,
      posY: 0,
      dragStartX: 0,
      dragStartY: 0,
      dragMouseX: 0,
      dragMouseY: 0,
      dragging: false,
      clickPrevented: false,
    }
  },
  computed: {
    formattedDuration() {
      const m = Math.floor(this.duration / 60)
      const s = this.duration % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    panelStyle() {
      if (this.posX === 0 && this.posY === 0) return {}
      return { transform: `translate(${this.posX}px, ${this.posY}px)` }
    },
  },
  beforeUnmount() {
    this._closeEventsWs()
  },
  methods: {
    onDragStart(e) {
      this.clickPrevented = false
      this.dragging = true
      const evt = e.touches ? e.touches[0] : e
      this.dragMouseX = evt.clientX
      this.dragMouseY = evt.clientY
      this.dragStartX = this.posX
      this.dragStartY = this.posY
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
      document.addEventListener('touchmove', this.onDragMove, { passive: true })
      document.addEventListener('touchend', this.onDragEnd)
    },
    onDragMove(e) {
      const evt = e.touches ? e.touches[0] : e
      const dx = evt.clientX - this.dragMouseX
      const dy = evt.clientY - this.dragMouseY
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.clickPrevented = true
      }
      this.posX = this.dragStartX + dx
      this.posY = this.dragStartY + dy
    },
    onDragEnd() {
      this.dragging = false
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
      document.removeEventListener('touchmove', this.onDragMove)
      document.removeEventListener('touchend', this.onDragEnd)
      // Defer clickPrevented reset so the click event fires while it's still true
      if (this.clickPrevented) {
        setTimeout(() => { this.clickPrevented = false }, 300)
      }
    },
    handleFabClick() {
      if (this.clickPrevented) return
      if (this.state === 'idle') {
        this.panelOpen = true
        this.showTranscript = true
        if (this.$refs.spinner) {
          this.$refs.spinner.handleToggle()
        }
      } else {
        this.showTranscript = !this.showTranscript
      }
    },

    handleClose() {
      this.showTranscript = false
      this.panelOpen = false
    },

    handleHangup() {
      if (this.$refs.spinner) {
        this.$refs.spinner.hangup()
      }
    },

    onStateChange(state) {
      this.state = state
      if (state === 'connected') {
        this.showTranscript = true
      }
      if (state === 'idle' || state === 'error') {
        this._closeEventsWs()
      }
    },

    onCallStart(data) {
      this.sessionId = data.sessionId
      this.duration = 0
      this.transcriptMessages = []
      this.vadState = 'listening'
      this.$emit('callStart', data)
      this._connectEventsWs(data.sessionId)
      this._startDurationTimer()
    },

    onCallEnd(data) {
      this._stopDurationTimer()
      this.sessionId = null
      this.vadState = 'idle'
      this.$emit('callEnd', data)
      this._closeEventsWs()
    },

    onError(err) {
      this.vadState = 'idle'
      this.$emit('error', err)
    },

    onStats(stats) {
      // stats logged by spinner
    },

    _connectEventsWs(sessionId) {
      this._closeEventsWs()
      const serverBase = this.voiceServerUrl || '/pwa-api'
      const host = serverBase.startsWith('http') ? serverBase.replace(/^https?:\/\//, '') : window.location.host
      const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url = `${wsProto}//${host}${serverBase === '/' ? '' : serverBase}/voice/events?sessionId=${sessionId}`
      this.eventsWs = new WebSocket(url)

      this.eventsWs.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data)
          this._handleWsEvent(msg)
        } catch (err) {
          // ignore parse errors
        }
      }

      this.eventsWs.onclose = () => {
        this.eventsWs = null
      }
    },

    _handleWsEvent(msg) {
      switch (msg.type) {
        case 'transcript.user':
          this._addMessage('user', msg.text)
          break
        case 'transcript.assistant':
          this._addMessage('assistant', msg.text)
          break
        case 'vad.state':
          this.vadState = msg.state
          this.callStatus = msg.state === 'user_speaking' ? 'You are speaking...'
            : msg.state === 'ai_speaking' ? 'AI is responding...'
            : msg.state === 'listening' ? 'Listening...'
            : ''
          break
        case 'tool.call':
          this.callStatus = `Running tool: ${msg.tool}...`
          this._addToolMessage(msg.tool)
          break
        case 'tool.result':
          this.callStatus = `Tool ${msg.tool} completed`
          this._addToolResult(msg)
          break
        case 'error':
          this._addMessage('assistant', `Error: ${msg.error}`)
          break
      }
    },

    _addMessage(role, text) {
      this.transcriptMessages.push({
        role,
        text,
        ts: Date.now(),
        tool: null,
      })
      this._scrollToBottom()
    },

    _addToolMessage(toolName) {
      const last = this.transcriptMessages[this.transcriptMessages.length - 1]
      if (last && last.role === 'assistant' && !last.tool) {
        last.tool = toolName
      }
      this._scrollToBottom()
    },
    _addToolResult(msg) {
      const last = this.transcriptMessages[this.transcriptMessages.length - 1]
      if (last && last.tool === msg.tool) {
        last.toolResult = msg.result
      }
      this._scrollToBottom()
    },

    _scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    _closeEventsWs() {
      if (this.eventsWs) {
        this.eventsWs.onclose = null
        this.eventsWs.close()
        this.eventsWs = null
      }
    },

    _startDurationTimer() {
      this._stopDurationTimer()
      this._durationInterval = setInterval(() => {
        this.duration++
      }, 1000)
    },

    _stopDurationTimer() {
      if (this._durationInterval) {
        clearInterval(this._durationInterval)
        this._durationInterval = null
      }
    },

    formatTime(ts) {
      const d = new Date(ts)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    formatToolResult(result) {
      if (!result) return ''
      if (typeof result === 'string') return result
      try {
        return JSON.stringify(result, null, 2)
      } catch {
        return String(result)
      }
    },
  },
}
</script>

<style scoped>
.voice-panel {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
}

.voice-panel__fab {
  width: 68px;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: 50%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 2px 12px rgba(0,77,157,0.15);
  cursor: grab;
}

.voice-panel__fab:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 20px rgba(0,77,157,0.25);
}

.voice-panel__fab--active {
  box-shadow: 0 0 20px rgba(2,134,229,0.3);
}

.voice-panel__fab--dragging {
  transition: none !important;
  transform: scale(1.1);
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  cursor: grabbing;
}



.voice-panel--dragging {
  transition: none !important;
}

.voice-panel__transcript {
  width: 380px;
  max-height: 520px;
  background: #1a1d23;
  border: 1px solid #2a2d35;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
}

.voice-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #2a2d35;
  flex-shrink: 0;
}

.voice-panel__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.voice-panel__header-icon {
  color: #0286E5;
  display: flex;
}

.voice-panel__header-title {
  color: #e8eaed;
  font-size: 14px;
  font-weight: 600;
}

.voice-panel__header-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #555;
  transition: background 0.3s;
}

.voice-panel__header-dot--connecting,
.voice-panel__header-dot--registering,
.voice-panel__header-dot--ringing {
  background: #0286E5;
  animation: dot-pulse 1s ease-in-out infinite;
}

.voice-panel__header-dot--connected {
  background: #34a853;
  animation: dot-pulse 1.8s ease-in-out infinite;
}

.voice-panel__header-dot--error {
  background: #dc3545;
}

.voice-panel__header-duration {
  color: #8eb6e3;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.voice-panel__header-actions {
  display: flex;
  gap: 4px;
}

.voice-panel__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  border-radius: 8px;
  transition: background 0.2s, opacity 0.2s;
}

.voice-panel__btn--icon {
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #8eb6e3;
}

.voice-panel__btn--icon:hover {
  background: #2a2d35;
  color: #e8eaed;
}

.voice-panel__btn--primary {
  padding: 10px 20px;
  background: #dc3545;
  color: #fff;
  font-weight: 600;
  justify-content: center;
  width: 100%;
}

.voice-panel__btn--primary:hover {
  background: #c82333;
}

.voice-panel__messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 200px;
  scrollbar-width: thin;
  scrollbar-color: #2a2d35 transparent;
}

.voice-panel__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #555;
  gap: 12px;
  text-align: center;
  font-size: 13px;
}

.voice-panel__message {
  display: flex;
  gap: 10px;
  animation: msg-in 0.3s ease;
}

.voice-panel__message--user {
  flex-direction: row-reverse;
}

.voice-panel__msg-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2a2d35;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8eb6e3;
  flex-shrink: 0;
}

.voice-panel__message--user .voice-panel__msg-avatar {
  background: #004d9d;
  color: #fff;
}

.voice-panel__msg-content {
  max-width: 80%;
}

.voice-panel__msg-role {
  font-size: 11px;
  color: #8eb6e3;
  margin-bottom: 2px;
  font-weight: 500;
}

.voice-panel__message--user .voice-panel__msg-role {
  text-align: right;
  color: #8eb6e3;
}

.voice-panel__msg-text {
  font-size: 13px;
  color: #e8eaed;
  line-height: 1.5;
  background: #2a2d35;
  padding: 8px 12px;
  border-radius: 12px;
  border-top-left-radius: 4px;
}

.voice-panel__message--user .voice-panel__msg-text {
  background: #004d9d;
  border-radius: 12px;
  border-top-right-radius: 4px;
}

.voice-panel__msg-tool {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #0286E5;
  margin-top: 4px;
  padding: 0 4px;
}

.voice-panel__msg-time {
  font-size: 10px;
  color: #555;
  margin-top: 2px;
  padding: 0 4px;
}

.voice-panel__tool-result {
  margin-top: 4px;
  padding: 6px 8px;
  background: #1a1d23;
  border: 1px solid #2a2d35;
  border-radius: 6px;
  font-size: 11px;
  color: #8eb6e3;
  max-height: 120px;
  overflow-y: auto;
}

.voice-panel__tool-result pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  color: #a0c4e8;
  line-height: 1.4;
}

.voice-panel__actions {
  padding: 12px 16px;
  border-top: 1px solid #2a2d35;
  flex-shrink: 0;
}

/* transitions */
.voice-slide-enter-active,
.voice-slide-leave-active {
  transition: all 0.25s ease;
}

.voice-slide-enter-from,
.voice-slide-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.96);
}

@keyframes msg-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes dot-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
</style>
